import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Promotion } from "@/services/admin/packages.interfaces";

interface DeletePromotionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  isDeleting: boolean;
  promotion: Promotion | null;
}

const formatDate = (date: Date | null) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatPrice = (price: number) => {
  return price.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const getPromotionPeriodText = (startDate: Date | null, endDate: Date | null) => {
  if (!startDate && !endDate) {
    return "Vigência contínua";
  }
  if (!startDate) {
    const formattedEndDate = formatDate(endDate);
    return formattedEndDate ? `Válido até ${formattedEndDate}` : "Vigência contínua";
  }
  if (!endDate) {
    const formattedStartDate = formatDate(startDate);
    return formattedStartDate ? `Válido a partir de ${formattedStartDate}` : "Vigência contínua";
  }
  const formattedStartDate = formatDate(startDate);
  const formattedEndDate = formatDate(endDate);
  return (formattedStartDate && formattedEndDate) 
    ? `${formattedStartDate} - ${formattedEndDate}` 
    : "Vigência contínua";
};

const DeletePromotionDialog: React.FC<DeletePromotionDialogProps> = ({
  isOpen,
  onOpenChange,
  onConfirm,
  isDeleting,
  promotion,
}) => {
  if (!promotion) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Remover Promoção</DialogTitle>
          <DialogDescription>
            Tem certeza que deseja remover esta promoção? Esta ação não pode ser desfeita.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="rounded-lg border border-gray-200 p-4 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Período</span>
              <span className="font-medium">
                {getPromotionPeriodText(promotion.startDate, promotion.endDate)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Valor</span>
              <span className="font-medium text-green-600">
                {formatPrice(promotion.price)}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Removendo...
              </>
            ) : (
              "Remover"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeletePromotionDialog;